import { useState, type FormEvent } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Field';
import { cn } from '@/lib/utils';

export const ROADMAP_MAX_CARDS = 3;

export interface RoadmapCard {
  horizon: string;
  bullets: string[];
}

interface Props {
  value: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void | Promise<void>;
  submitting?: boolean;
  onCancel?: () => void;
}

function parseItems(value: Record<string, unknown>): RoadmapCard[] {
  const raw = Array.isArray(value?.items) ? (value.items as RoadmapCard[]) : [];
  return raw.map((it: RoadmapCard) => ({
    horizon: typeof it.horizon === 'string' ? it.horizon : '',
    bullets: Array.isArray(it.bullets) ? (it.bullets as string[]).filter((b) => typeof b === 'string') : [],
  }));
}

export function RoadmapEditForm({ value, onChange, submitting, onCancel }: Props) {
  const [items, setItems] = useState<RoadmapCard[]>(parseItems(value));
  const [errors, setErrors] = useState<Record<number, Record<string, string>>>({});

  function patchItem(idx: number, patch: Partial<RoadmapCard>) {
    setItems((cur) => cur.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function addItem() {
    if (items.length >= ROADMAP_MAX_CARDS) return;
    setItems((cur) => [...cur, { horizon: '', bullets: [''] }]);
  }

  function removeItem(idx: number) {
    setItems((cur) => cur.filter((_, i) => i !== idx));
    setErrors((cur) => {
      const next: Record<number, Record<string, string>> = {};
      for (const [k, v] of Object.entries(cur)) {
        const i = Number(k);
        if (i < idx) next[i] = v;
        else if (i > idx) next[i - 1] = v;
      }
      return next;
    });
  }

  function addBullet(cardIdx: number) {
    setItems((cur) =>
      cur.map((it, i) =>
        i === cardIdx ? { ...it, bullets: [...it.bullets, ''] } : it,
      ),
    );
  }

  function patchBullet(cardIdx: number, bulletIdx: number, val: string) {
    setItems((cur) =>
      cur.map((it, i) =>
        i === cardIdx
          ? { ...it, bullets: it.bullets.map((b, j) => (j === bulletIdx ? val : b)) }
          : it,
      ),
    );
  }

  function removeBullet(cardIdx: number, bulletIdx: number) {
    setItems((cur) =>
      cur.map((it, i) =>
        i === cardIdx ? { ...it, bullets: it.bullets.filter((_, j) => j !== bulletIdx) } : it,
      ),
    );
  }

  function validate(): boolean {
    const next: Record<number, Record<string, string>> = {};
    items.forEach((it, idx) => {
      const itemErr: Record<string, string> = {};
      if (!it.horizon.trim()) itemErr.horizon = 'Horizon title is required';
      const validBullets = it.bullets.filter((b) => b.trim());
      if (validBullets.length === 0) itemErr.bullets = 'At least one bullet is required';
      if (Object.keys(itemErr).length > 0) next[idx] = itemErr;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!validate()) return;
    const cleaned = items.map((it) => ({
      horizon: it.horizon.trim(),
      bullets: it.bullets.filter((b) => b.trim()),
    }));
    onChange({ items: cleaned });
  }

  const atMax = items.length >= ROADMAP_MAX_CARDS;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Roadmap</p>
          <p className="mt-1 text-sm text-fg-muted">
            {items.length} of {ROADMAP_MAX_CARDS} cards — "Where we're going." section
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={addItem}
          disabled={atMax}
        >
          Add card
        </Button>
      </div>

      {atMax && (
        <p className="text-xs text-fg-subtle" role="status">
          Maximum of {ROADMAP_MAX_CARDS} cards. Remove one to add another.
        </p>
      )}

      {items.length === 0 && (
        <div className="rounded-md border border-dashed border-border bg-bg-elevated/40 px-6 py-10 text-center text-sm text-fg-subtle">
          No roadmap cards yet. Click "Add card" to begin.
        </div>
      )}

      <ul className="space-y-4">
        {items.map((item, idx) => {
          const itemErr = errors[idx] ?? {};
          return (
            <li
              key={idx}
              className={cn(
                'rounded-md border bg-bg-elevated/40 p-4',
                Object.keys(itemErr).length > 0 ? 'border-danger/40' : 'border-border',
              )}
            >
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs font-medium text-fg-muted">Card {idx + 1}</span>
                <button
                  type="button"
                  aria-label={`Remove card ${idx + 1}`}
                  onClick={() => removeItem(idx)}
                  className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs text-fg-muted hover:bg-danger/10 hover:text-danger"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Remove
                </button>
              </div>

              <div className="mb-3">
                <Field id={`roadmap-${idx}-horizon`} label="Horizon title" required error={itemErr.horizon}>
                  <Input
                    id={`roadmap-${idx}-horizon`}
                    value={item.horizon}
                    onChange={(e) => patchItem(idx, { horizon: e.target.value })}
                    placeholder="Next 6 months"
                  />
                </Field>
              </div>

              <div>
                <p className="mb-2 text-xs font-medium text-fg-muted">Bullets</p>
                {itemErr.bullets && (
                  <p className="mb-2 text-xs text-danger">{itemErr.bullets}</p>
                )}
                <ul className="space-y-2">
                  {item.bullets.map((bullet, bIdx) => (
                    <li key={bIdx} className="flex items-center gap-2">
                      <Input
                        value={bullet}
                        onChange={(e) => patchBullet(idx, bIdx, e.target.value)}
                        placeholder="e.g. Launch the Vonlof Open Source programme"
                        className="flex-1"
                      />
                      <button
                        type="button"
                        aria-label="Remove bullet"
                        onClick={() => removeBullet(idx, bIdx)}
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-fg-muted hover:bg-danger/10 hover:text-danger"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  leftIcon={<Plus className="h-3.5 w-3.5" />}
                  onClick={() => addBullet(idx)}
                  className="mt-2"
                >
                  Add bullet
                </Button>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" loading={submitting} disabled={items.length === 0}>
          Save changes
        </Button>
      </div>
    </form>
  );
}
